import React, { useState, useEffect, useContext, useRef } from 'react';
import { MenuList } from '@/components/menuList';
import { Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Box, Flex, Text, Divider, Tag, TagLabel, useDisclosure, useToast, IconButton, Skeleton, Menu, MenuButton, MenuItem, Spinner } from '@chakra-ui/react';
import { MdPersonAdd, MdArrowBack, MdArrowDropDown } from 'react-icons/md';
import SharedExperienceIcon from '../../../components/icons/sharedExperienceIcon';
import { Select } from 'chakra-react-select';
import { useParams } from 'react-router-dom';
import { useSharedExperiencesApi } from '../../lib/useSharedExperiencesApi';
import { AuthContext } from '../../../auth/lib/authContext';
import { ProfileContext } from '../../../profile/lib/profileContext';
import AddProfileInput from '../../../profile/component/AddProfileInput';
import ProfileProvider from '../../../profile/lib/profileProvider';
import ProfileThumbnail from '../../../profile/component/profileThumbnail';
import SharedExperienceMemberListItem from './sharedExperienceMemberListItem';
import SharedExperienceUserTempListItem from './sharedExperienceUserTempListItem';
import SharedExperienceSelectedMembers from './sharedExperienceSelectedMembers';
import UnlockSharingFeatures from '../../../profile/component/unlockSharingFeatures';
import memberStatusDictionnary from '../../../lib/memberStatusDictionnary';
import roleDictionnary from '../../../lib/roleDictionnary';
import { useForm } from 'react-hook-form';
import useTranslation from '../../../lib/i18n/useTranslation';
import { OrganisationContext } from '../../../organisation/lib/organisationContext';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';

export default function SharedExperienceMembers() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { profile } = useContext(AuthContext);
  const { organisation, hasOrganisation } = useContext(OrganisationContext);
  const { sharedExperience, refreshSharedExperience } = useContext(SharedExperiencesContext);
  const { profileSystem } = useContext(ProfileContext);
  const [profiles, setProfiles] = useState(null);
  const [user_temp, setUserTemp] = useState(null);
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null);
  const [hasRequestedEdition, setHasRequestedEdition] = useState(false);
  const [rights, setRights] = useState({ can_update: false });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingAccessLevel, setIsUpdatingAccessLevel] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);
  const [membersRole, setMembersRole] = useState(roleDictionnary.contributor);
  const membersModal = useDisclosure();
  const addMembersModal = useDisclosure();
  const unlockSharingFeaturesModal = useDisclosure();
  const requestEditModal = useDisclosure();
  const modaleRef = useRef();
  const modaleAddRef = useRef();
  const toast = useToast();
  const formMember = useForm({ mode: 'onChange' });
  const formAddMember = useForm({ mode: 'onChange' });
  const sharedExperiencesApi = useSharedExperiencesApi();
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const membersData = await sharedExperiencesApi.fetchMembersData(id, profile.id);
      setProfiles(membersData.profiles || []);
      setUserTemp(membersData.userTemp || []);
      setRights(membersData.rights?.[0] || { can_update: false });
      setRole(membersData.role);
      setStatus(membersData.status);
      setHasRequestedEdition(membersData.hasRequestedEdition || false);
    } catch (error) {
      console.error('Error fetching members data:', error);
      toast({
        title: t('common.error'),
        description: t('members.loadFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const updateAccessLevel = async (accessLevel) => {
    try {
      setIsUpdatingAccessLevel(true);
      await sharedExperiencesApi.updateAccessLevel(accessLevel, id);
      await refreshSharedExperience();
      setIsUpdatingAccessLevel(false);
    } catch (error) {
      console.error('Error updating access level:', error);
      setIsUpdatingAccessLevel(false);
    }
  };
  const onAddFirstUser = async (email) => {
    try {
      const newSelectedMembers = Array.from(selectedMembers);
      newSelectedMembers.push(email);
      setSelectedMembers(newSelectedMembers);
      addMembersModal.onOpen();
      membersModal.onClose();
    } catch (e) {
      console.error(e);
    }
  };
  const resetModal = () => {
    addMembersModal.onClose();
    membersModal.onOpen();
    formMember.setValue('people', '');
    setSelectedMembers([]);
  };
  const addNewUser = async (email) => {
    try {
      const newSelectedMembers = Array.from(selectedMembers);
      if (newSelectedMembers.indexOf(email) === -1) {
        newSelectedMembers.push(email);
      }
      formAddMember.reset();
      setSelectedMembers(newSelectedMembers);
      addMembersModal.onOpen();
      membersModal.onClose();
    } catch (e) {
      console.error(e);
    }
  };
  const addMembers = async () => {
    try {
      setAddingMembers(true);
      await sharedExperiencesApi.addMembers(selectedMembers, id, membersRole);
      await fetchData();
      resetModal();
      setAddingMembers(false);
    } catch (error) {
      console.error('Error adding members:', error);
      setAddingMembers(false);
    }
  };
  const requestEdit = async () => {
    try {
      await sharedExperiencesApi.requestEdit(id, profile.id);
      await fetchData();
      requestEditModal.onClose();
      toast({
        title: t('common.success'),
        description: t('members.editRequestSent'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error requesting edit:', error);
      toast({
        title: 'Error',
        description: 'Failed to send edit request',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  return (
    <>
      <Flex alignItems="center">
        {isLoading ? (
          <Skeleton height="40px" width="120px" ml={2} />
        ) : (
          <>
            {profiles && (
              <Flex
                position={'relative'}
                cursor={'pointer'}
                onClick={membersModal.onOpen}
                w={(((profiles.length * 20) + 10) + 'px')}
              >
                {profileSystem && profileSystem.role !== 'standard' && profiles.map((p, i) => {
                  return (
                    <Box
                      key={i}
                      position={'relative'}
                      left={i > 0 ? (-10 * i + 'px') : null} >
                      <ProfileProvider
                        profileId={p.id}
                        originalProfile={p}>
                        <ProfileThumbnail size={30} />
                      </ProfileProvider>
                    </Box>
                  )
                })}
              </Flex>
            )}
            {(role && role !== roleDictionnary.viewer && status !== memberStatusDictionnary.requested) && (
              <>
                <Button
                  variant="greenFill"
                  leftIcon={<MdPersonAdd />}
                  ml={2}
                  onClick={() => {
                    if (profileSystem && profileSystem.role !== 'standard') {
                      membersModal.onOpen();
                    } else {
                      unlockSharingFeaturesModal.onOpen();
                    }
                  }}
                >
                  Share
                </Button>
                <UnlockSharingFeatures item="shared_experience" disclosure={unlockSharingFeaturesModal} />
              </>
            )}
            {(role && role !== roleDictionnary.viewer && status === memberStatusDictionnary.requested) && (
              <Tag variant="orange">
                <TagLabel>{t('members.editionRequested')}</TagLabel>
              </Tag>
            )}
            {((!role || (role && role === roleDictionnary.viewer)) && !hasRequestedEdition) && (
              <Button
                variant="greyOutline"
                ml={2}
                onClick={requestEditModal.onOpen}
              >
                {t('sharedExperience.requestToEdit')}
              </Button>
            )}
            {((!role || (role && role === roleDictionnary.viewer)) && hasRequestedEdition) && (
              <Tag variant="orange" ml={2}>
                <TagLabel>{t('members.editionRequestPending')}</TagLabel>
              </Tag>
            )}
          </>
        )}
      </Flex>
      {/* Members Modal */}
      <Modal
        isOpen={membersModal.isOpen}
        onClose={membersModal.onClose}
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent ref={modaleRef}>
          <ModalHeader>{t('members.members')}</ModalHeader>
          <ModalBody p={3}>
            {(role && role !== roleDictionnary.viewer) && profileSystem && profileSystem.role !== 'standard' && (
              <Box mb={2}>
                <AddProfileInput
                  placeholder={t('members.addPeoplePlaceholder')}
                  clickTarget={modaleRef}
                  variant="greenOutline"
                  onChange={(e) => {
                    onAddFirstUser(e.email);
                  }}
                />
              </Box>
            )}
            <Box
              borderWidth="1px"
              borderColor="divider.grey"
              borderRadius="2px"
              borderStyle={'solid'}
            >
              {profiles && profiles.length > 0 && profileSystem && profileSystem.role !== 'standard' && profiles.map((p, i) => (
                <Box key={i}>
                  {i > 0 && <Divider />}
                  <ProfileProvider
                    profileId={p.id}
                    originalProfile={p}
                  >
                    <SharedExperienceMemberListItem
                      sharedExperienceId={id}
                      onRevoke={() => {
                        setProfiles(null);
                        fetchData();
                      }}
                      onRoleUpdated={() => {
                        setProfiles(null);
                        fetchData();
                      }}
                    />
                  </ProfileProvider>
                </Box>
              ))}
              {(!profiles || profiles.length === 0) && (
                <Box p={3} textAlign="center">
                  <Text color="text.light.grey">{t('sharedExperience.noMembersFound')}</Text>
                </Box>
              )}
              {profileSystem && profileSystem.role === 'standard' && (
                <Box p={3} textAlign="center">
                  <Text color="text.light.grey">
                    Upgrade your plan to see and manage members
                  </Text>
                </Box>
              )}
              {user_temp && user_temp.length > 0 && profileSystem && profileSystem.role !== 'standard' && (
                <>
                  <Divider />
                  {user_temp.map((u, i) => (
                    <Box key={i}>
                      {i > 0 && <Divider />}
                      <SharedExperienceUserTempListItem userTemp={u} />
                    </Box>
                  ))}
                </>
              )}
            </Box>
            {hasOrganisation && (role && role !== roleDictionnary.viewer) && profileSystem && profileSystem.role !== 'standard' && (
              <Flex
                border="1px solid"
                borderColor="divider.grey"
                borderRadius="2px"
                p={3}
                backgroundColor={"white"}
                mt={1}
                flexDirection={"column"}
                gap={2}
              >
                <Text>{t('members.generalAccess')}</Text>
                <Flex
                  flexDirection={"column"}
                  width="100%"
                >
                  {!isUpdatingAccessLevel && (
                    <Menu ml={2} >
                      {({ isOpen }) => (
                        <>
                          <MenuButton
                            isActive={isOpen}
                            as={Button}
                            rightIcon={<MdArrowDropDown />}
                            variant='transparent'
                            width={"fit-content"}
                            textTransform={'capitalize'}
                            flexShrink={0}
                            px={0}
                            fontSize={"14px"}
                          >
                            {sharedExperience?.access_level}
                          </MenuButton>
                          <MenuList>
                            {(sharedExperience?.access_level === 'internal') && <MenuItem onClick={() => {
                              updateAccessLevel('private')
                            }} >Private</MenuItem>}
                            {(sharedExperience?.access_level === 'private') && <MenuItem onClick={() => {
                              updateAccessLevel('internal')
                            }}>Internal</MenuItem>}
                          </MenuList>
                        </>
                      )}
                    </Menu>
                  )}
                  {isUpdatingAccessLevel && (
                    <Spinner />
                  )}
                  {sharedExperience?.access_level === 'internal' &&
                    <Text color={"text.medium.grey"} >
                      All users in {organisation.name} can view this shared experience, but you need to add them to allow editing.
                    </Text>
                  }
                  {sharedExperience?.access_level === 'private' &&
                    <Text color={"text.medium.grey"} >
                      No users in {organisation.name} can view or edit this shared experience. Switch to 'Internal' to allow them to see it.
                    </Text>
                  }
                </Flex>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* Add Members Modal */}
      <Modal
        isOpen={addMembersModal.isOpen}
        onClose={addMembersModal.onClose}
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent ref={modaleAddRef}>
          <ModalHeader>
            <IconButton
              fontSize='24'
              variant="tertiary"
              icon={<MdArrowBack />}
              mr={2}
              onClick={resetModal}
            />
            {t('members.members')}
          </ModalHeader>
          <ModalBody p={3}>
            <Box>
              <SharedExperienceSelectedMembers
                selectedMembers={selectedMembers}
                onRemoveSelectedMember={(i) => {
                  let newSelectedMembers = Array.from(selectedMembers);
                  newSelectedMembers.splice(i, 1);
                  if (newSelectedMembers.length < 1) {
                    resetModal();
                  }
                  setSelectedMembers(newSelectedMembers);
                }}
              />
              <Box mb={2}>
                <Select
                  options={[
                    { value: roleDictionnary.viewer, label: roleDictionnary.viewer },
                    { value: roleDictionnary.contributor, label: roleDictionnary.contributor },
                  ]}
                  defaultValue={{ value: roleDictionnary.contributor, label: roleDictionnary.contributor }}
                  onChange={(option) => { setMembersRole(option.value) }}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  chakraStyles={{
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? 'text.disable.grey !important' : provided.backgroundColor,
                      color: 'text.dark.grey !important',
                    }),
                  }}
                />
              </Box>
              <Flex alignItems={'center'}>
                <AddProfileInput
                  placeholder={t('members.addPeoplePlaceholder')}
                  clickTarget={modaleAddRef}
                  variant="greenOutline"
                  onChange={(e) => {
                    addNewUser(e.email);
                  }}
                />
              </Flex>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="greenOutline"
              ml={2}
              onClick={resetModal}
              disabled={addingMembers}
            >{t('common.back')}</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={addingMembers}
              onClick={addMembers}
            >{t('common.send')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Request Edit Modal */}
      <Modal isOpen={requestEditModal.isOpen} onClose={requestEditModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('sharedExperience.requestToEdit')}</ModalHeader>
          <ModalBody>
            <Text>
              You are requesting edit access to this shared experiences. The owner will be notified and can approve or decline your request.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="greyOutline" onClick={requestEditModal.onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="greenFill" ml={2} onClick={requestEdit}>
              {t('common.send')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
} 